import { useState } from 'react';
import { Link } from 'react-router';
import { AlertCircle, CheckCircle2, X } from 'lucide-react';
import { useCancelSubscription, useResumeSubscription, useSubscription } from '@/hooks/useBilling';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AppSheet } from '@/components/ui/app-sheet';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { parseTimestamp } from '@/utils/datetime';

const STATUS_STYLE: Record<string, string> = {
  active: 'bg-sev-4/15 text-sev-4',
  past_due: 'bg-sev-3/20 text-foreground',
  canceled: 'bg-sev-1/15 text-sev-1',
  trialing: 'bg-sev-5/15 text-sev-5',
};

function formatCurrency(amount: string | number): string {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
}

function formatDate(dateString: string | null): string {
  // Use parseTimestamp so a backend value that's missing the `Z`
  // suffix (legacy serialization or partial deploy) still renders
  // as the correct local calendar day instead of being silently
  // shifted by the user's UTC offset.
  const date = parseTimestamp(dateString);
  if (!date) return 'N/A';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

interface KpiCardProps {
  label: string;
  value: React.ReactNode;
  variant?: 'default' | 'accent' | 'warning';
}

function KpiCard({ label, value, variant = 'default' }: KpiCardProps) {
  return (
    <div
      className={cn(
        'rounded-md border p-3',
        variant === 'accent'
          ? 'border-sev-5/30 bg-sev-5/5'
          : variant === 'warning'
            ? 'border-sev-3/30 bg-sev-3-soft/40'
            : 'border-border bg-bg-subtle',
      )}
    >
      <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="text-base font-semibold mt-1">{value}</div>
    </div>
  );
}

const CANCEL_REASONS: { key: string; label: string }[] = [
  { key: 'too_expensive', label: 'Too expensive' },
  { key: 'missing_features', label: "Missing features I need" },
  { key: 'too_complicated', label: 'Too hard to set up or use' },
  { key: 'switched', label: 'Switched to another product' },
  { key: 'not_needed', label: 'No longer need it' },
  { key: 'other', label: 'Other' },
];

export default function SubscriptionManagement() {
  const { data: subscription, isLoading, error } = useSubscription();
  const cancelMutation = useCancelSubscription();
  const resumeMutation = useResumeSubscription();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(true);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelComment, setCancelComment] = useState('');

  const handleConfirmCancel = async () => {
    try {
      await cancelMutation.mutateAsync({
        at_period_end: cancelAtPeriodEnd,
        reason: cancelReason,
        comment: cancelComment.trim() || undefined,
      });
      setShowCancelConfirm(false);
    } catch (err) {
      console.error('Failed to cancel subscription:', err);
    }
  };

  const handleResume = async () => {
    try {
      await resumeMutation.mutateAsync();
    } catch (err) {
      console.error('Failed to resume subscription:', err);
    }
  };

  if (error) {
    return (
      <section className="bg-card border border-border rounded-lg p-5">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>Error loading subscription:</strong> {error.message}
          </AlertDescription>
        </Alert>
      </section>
    );
  }

  if (isLoading) {
    return (
      <section className="bg-card border border-border rounded-lg p-5">
        <Skeleton className="h-32 w-full" />
      </section>
    );
  }

  if (!subscription) {
    return (
      <section className="bg-card border border-border rounded-lg p-5">
        <Alert>
          <AlertDescription>No subscription information available.</AlertDescription>
        </Alert>
      </section>
    );
  }

  const hasActiveSubscription =
    subscription.stripe_subscription_id && subscription.subscription_status !== 'canceled';
  const isTrialing = subscription.subscription_status === 'trialing';
  const isCanceled = subscription.subscription_status === 'canceled';
  const pendingCancel = Boolean(hasActiveSubscription) && subscription.cancel_at_period_end;

  return (
    <section className="bg-card border border-border rounded-lg p-5">
      <h2 className="text-base font-semibold mb-4">Subscription details</h2>

      {cancelMutation.isError ? (
        <Alert variant="destructive" className="mb-4 relative">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>Cancellation failed:</strong>{' '}
            {cancelMutation.error.message || 'An unexpected error occurred.'}
          </AlertDescription>
          <button
            type="button"
            onClick={() => { cancelMutation.reset(); }}
            className="absolute top-2 right-2 inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent"
            aria-label="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </Alert>
      ) : null}

      {resumeMutation.isError ? (
        <Alert variant="destructive" className="mb-4 relative">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>Resume failed:</strong>{' '}
            {resumeMutation.error.message || 'An unexpected error occurred.'}
          </AlertDescription>
          <button
            type="button"
            onClick={() => { resumeMutation.reset(); }}
            className="absolute top-2 right-2 inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent"
            aria-label="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </Alert>
      ) : null}

      {cancelMutation.isSuccess ? (
        <Alert className="mb-4 border-sev-4 bg-sev-4/10">
          <CheckCircle2 className="h-4 w-4" />
          <AlertDescription>
            <strong>Subscription canceled successfully.</strong>
            <p className="text-sm mt-0.5">
              {cancelAtPeriodEnd
                ? 'Your plan stays active until the end of the current billing period, then moves to Free. No further charges.'
                : "Your plan has been canceled and you're now on the Free plan."}
            </p>
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <KpiCard label="Current plan" value={subscription.plan_name} />
        <KpiCard
          label="Status"
          value={
            <Badge
              className={cn(
                'border-0 font-normal',
                STATUS_STYLE[subscription.subscription_status ?? ''] ??
                  'bg-muted text-muted-foreground',
              )}
            >
              {subscription.subscription_status?.toUpperCase() ?? 'FREE'}
            </Badge>
          }
        />
        <KpiCard
          label="Billing term"
          value={
            subscription.billing_term
              ? subscription.billing_term.charAt(0).toUpperCase() +
                subscription.billing_term.slice(1)
              : 'N/A'
          }
        />
        <KpiCard label="Base cost" value={formatCurrency(subscription.base_cost)} />
      </div>

      {hasActiveSubscription ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
            <KpiCard
              variant="accent"
              label="Current period"
              value={
                <div className="text-sm font-normal">
                  <div>
                    <strong>Start:</strong>{' '}
                    {formatDate(subscription.subscription_period_start)}
                  </div>
                  <div>
                    <strong>End:</strong>{' '}
                    {formatDate(subscription.subscription_period_end)}
                  </div>
                </div>
              }
            />
            {subscription.next_billing_date ? (
              <KpiCard
                variant="accent"
                label="Next billing"
                value={formatDate(subscription.next_billing_date)}
              />
            ) : null}
            {isTrialing && subscription.trial_end ? (
              <KpiCard
                variant="warning"
                label="Trial ends"
                value={formatDate(subscription.trial_end)}
              />
            ) : null}
          </div>

          {pendingCancel ? (
            <div className="mb-3 rounded-md border border-sev-3/30 bg-sev-3-soft/40 p-3 text-sm">
              <strong>Scheduled to cancel.</strong> Your subscription stays active until{' '}
              {formatDate(subscription.subscription_period_end)}, then moves to the Free plan.
              Resume any time before then to keep it.
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2">
            <Button asChild>
              <Link to="/admin/plans">Change plan</Link>
            </Button>
            {pendingCancel ? (
              <Button
                onClick={() => { void handleResume(); }}
                disabled={resumeMutation.isPending}
              >
                {resumeMutation.isPending ? 'Resuming…' : 'Resume subscription'}
              </Button>
            ) : !isCanceled ? (
              <Button
                variant="outline"
                onClick={() => { setShowCancelConfirm(true); }}
                disabled={cancelMutation.isPending}
                className="text-destructive hover:text-destructive border-destructive/30 hover:bg-destructive/10"
              >
                Cancel subscription
              </Button>
            ) : null}
          </div>
        </>
      ) : (
        <div className="rounded-md border border-sev-5/30 bg-sev-5/5 p-4">
          <p className="text-sm mb-3">
            You're currently on the <strong>{subscription.plan_name}</strong> plan.
          </p>
          <Button asChild>
            <Link to="/admin/plans">Upgrade to a paid plan</Link>
          </Button>
        </div>
      )}

      <AppSheet
        open={showCancelConfirm}
        onOpenChange={setShowCancelConfirm}
        title="Cancel subscription"
        description={`Are you sure you want to cancel your subscription to the ${subscription.plan_name} plan?`}
      >
        <AppSheet.Body>
          <RadioGroup
            value={cancelAtPeriodEnd ? 'period-end' : 'immediate'}
            onValueChange={(v) => { setCancelAtPeriodEnd(v === 'period-end'); }}
            className="space-y-2"
          >
            <label
              className={cn(
                'flex items-start gap-3 rounded-md border p-3 cursor-pointer',
                cancelAtPeriodEnd ? 'border-primary bg-brand-soft/40' : 'border-border',
              )}
            >
              <RadioGroupItem value="period-end" className="mt-0.5" />
              <div className="text-sm">
                <strong>Cancel at period end</strong>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Stays active until {formatDate(subscription.subscription_period_end)} — you keep
                  everything you've paid for. No further charges after that, then you move to the
                  Free plan.
                </p>
              </div>
            </label>
            <label
              className={cn(
                'flex items-start gap-3 rounded-md border p-3 cursor-pointer',
                !cancelAtPeriodEnd ? 'border-destructive bg-destructive/5' : 'border-border',
              )}
            >
              <RadioGroupItem value="immediate" className="mt-0.5" />
              <div className="text-sm">
                <strong>Cancel immediately</strong>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Your paid plan ends now and you move to the Free plan today. No refund for the
                  rest of the current period.
                </p>
              </div>
            </label>
          </RadioGroup>

          <div className="mt-5">
            <p className="text-sm font-medium">What's the main reason you're cancelling?</p>
            <RadioGroup
              value={cancelReason}
              onValueChange={setCancelReason}
              className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5"
            >
              {CANCEL_REASONS.map((r) => (
                <label
                  key={r.key}
                  className={cn(
                    'flex items-center gap-2.5 rounded-md border px-3 py-2 cursor-pointer text-sm',
                    cancelReason === r.key ? 'border-primary bg-brand-soft/40' : 'border-border',
                  )}
                >
                  <RadioGroupItem value={r.key} />
                  {r.label}
                </label>
              ))}
            </RadioGroup>
            <Textarea
              className="mt-3"
              rows={3}
              maxLength={2000}
              placeholder={
                cancelReason === 'other'
                  ? 'Tell us a bit more…'
                  : 'Anything else we should know? (optional)'
              }
              value={cancelComment}
              onChange={(e) => { setCancelComment(e.target.value); }}
            />
          </div>
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button
            variant="outline"
            onClick={() => { setShowCancelConfirm(false); }}
            disabled={cancelMutation.isPending}
          >
            Keep subscription
          </Button>
          <Button
            onClick={() => { void handleConfirmCancel(); }}
            disabled={
              cancelMutation.isPending ||
              !cancelReason ||
              (cancelReason === 'other' && !cancelComment.trim())
            }
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {cancelMutation.isPending ? 'Cancelling…' : 'Confirm cancellation'}
          </Button>
        </AppSheet.Footer>
      </AppSheet>
    </section>
  );
}
